if (!customElements.get('quantity-btns')) {
  class QuantityBtns extends HTMLElement {
    connectedCallback() {
      this.buttons = Array.from(this.querySelectorAll('.quantity-btns__btn[data-quantity]'));
      if (this.buttons.length === 0) return;

      this.productContainer = this.closest('.js-product') || this.closest('.section-main-product');
      this.priceData = QuantityBtns.parsePriceData(this);

      this.boundHandleClick = this.handleClick.bind(this);
      this.boundHandleVariantChange = this.handleVariantChange.bind(this);
      this.boundSyncActiveFromInput = this.syncActiveFromInput.bind(this);

      this.addEventListener('click', this.boundHandleClick);
      if (this.productContainer) {
        this.productContainer.addEventListener('on:variant:change', this.boundHandleVariantChange);
      }

      const input = this.quantityInput;
      if (input) {
        input.addEventListener('change', this.boundSyncActiveFromInput);
        input.addEventListener('input', this.boundSyncActiveFromInput);
      }

      this.syncActiveFromInput();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.boundHandleClick);
      if (this.productContainer) {
        this.productContainer.removeEventListener('on:variant:change', this.boundHandleVariantChange);
      }

      const input = this.quantityInput;
      if (input) {
        input.removeEventListener('change', this.boundSyncActiveFromInput);
        input.removeEventListener('input', this.boundSyncActiveFromInput);
      }
    }

    /**
     * Reads the per-variant formatted tier prices rendered by the snippet.
     * @param {Element} el - The quantity-btns element.
     * @returns {object}
     */
    static parsePriceData(el) {
      const script = el.querySelector('script[type="application/json"]');
      if (!script) return {};

      try {
        return JSON.parse(script.textContent);
      } catch (e) {
        return {};
      }
    }

    /**
     * The quantity input the buttons write to and read from.
     * @returns {?Element}
     */
    get quantityInput() {
      const scope = this.productContainer || document;
      return scope.querySelector('.quantity-wrapper input[name="quantity"]')
        || scope.querySelector('input[name="quantity"]');
    }

    /**
     * Writes the clicked tier quantity into the quantity input.
     * @param {object} evt - Event object.
     */
    handleClick(evt) {
      const btn = evt.target.closest('.quantity-btns__btn[data-quantity]');
      if (!btn || !this.contains(btn)) return;
      evt.preventDefault();

      const qty = parseInt(btn.dataset.quantity, 10);
      const input = this.quantityInput;
      if (!input || Number.isNaN(qty)) return;

      input.value = qty;
      input.dispatchEvent(new CustomEvent('change', { bubbles: true, cancelable: false }));
    }

    /**
     * Swaps the tier prices to those of the newly selected variant.
     * @param {object} evt - Event object.
     */
    handleVariantChange(evt) {
      const variant = evt.detail ? evt.detail.variant : null;
      this.hidden = !variant;
      if (!variant) return;

      const prices = this.priceData[variant.id];
      if (!prices) return;

      this.buttons.forEach((btn) => {
        const tier = prices[btn.dataset.quantity];
        if (!tier) return;

        const priceEl = btn.querySelector('[data-qb-price]');
        const oldPriceEl = btn.querySelector('[data-qb-old-price]');

        if (priceEl) priceEl.innerHTML = tier.price;
        if (oldPriceEl) {
          oldPriceEl.innerHTML = tier.oldPrice || '';
          oldPriceEl.hidden = !tier.oldPrice;
        }
      });
    }

    /**
     * Marks the tier matching the quantity input's current value as active.
     */
    syncActiveFromInput() {
      const input = this.quantityInput;
      const currentQty = input ? parseInt(input.value, 10) : NaN;

      this.buttons.forEach((btn) => {
        const isActive = !Number.isNaN(currentQty) && parseInt(btn.dataset.quantity, 10) === currentQty;
        btn.classList.toggle('is-active', isActive);
        btn.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });
    }
  }

  customElements.define('quantity-btns', QuantityBtns);
}
